/* Real HTTP API implementation of TrainingClient */
import { ITrainingClient } from './ITrainingClient';
import {
  TrainingCategoryDto,
  CreateTrainingCategoryDto,
  UpdateTrainingCategoryDto,
  TrainingModuleDto,
  TrainingModuleDetailDto,
  CreateTrainingModuleDto,
  UpdateTrainingModuleDto,
  TrainingAssignmentDto,
  SubmitQuizDto,
  QuizResultDto,
  TrainingCertificateDto,
  PersonaOptionDto,
  UserOptionDto,
  StoreOptionDto,
  TrainingFranchiseOptionDto,
  TrainingModuleSectionDetailDto,
  CreateSectionRequestDto,
  UpdateSectionRequestDto,
  CreateQuizStandaloneRequestDto,
  UpdateQuizStandaloneRequestDto,
  PagedData,
  TrainingAssignmentListFilters,
} from './trainingTypes';
import { generateSummaryHtmlPreview, generateCompletionHtmlPreview } from '../../../../../utils/trainingReportHtml';

export class TrainingClientApi implements ITrainingClient {
  private baseUrl: string;

  constructor(baseUrl: string = '/api/Training') {
    this.baseUrl = baseUrl;
  }

  // Helper method to get auth headers with Clerk token
  private async getHeaders(): Promise<HeadersInit> {
    const token = await window.Clerk?.session?.getToken?.({ template: 'Orderly_default' });

    let store: { storeNumber?: string; franchiseId?: number } | null = null;
    const storedStore = localStorage.getItem('selectedStore');
    if (storedStore) {
      try {
        store = JSON.parse(storedStore);
      } catch {
        store = null;
      }
    }

    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(store && {
        'X-Context-Store-Number': store.storeNumber || '',
        'X-Context-Franchise-Id': store.franchiseId ? String(store.franchiseId) : '',
      }),
    };
  }

  // Extracts a human-readable message from ASP.NET Core error responses.
  // Handles both custom { message } payloads and RFC 7807 Problem Details { detail, title }.
  private async extractErrorMessage(response: Response, fallback: string): Promise<string> {
    try {
      const errorData = await response.json();
      return errorData.message || errorData.detail || errorData.title || fallback;
    } catch {
      return fallback;
    }
  }

  // ==================== Accessible Franchises ====================

  async getAccessibleFranchises(franchiseId?: number): Promise<TrainingFranchiseOptionDto[]> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();
    if (franchiseId) params.append('franchiseId', franchiseId.toString());
    const query = params.toString();
    const url = query ? `${this.baseUrl}/Modules/accessible-franchises?${query}` : `${this.baseUrl}/Modules/accessible-franchises`;
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error('Failed to fetch accessible franchises');
    const result = await response.json();
    return result.data;
  }

  // ==================== Training Categories ====================

  async getCategories(opts?: { includeInactive?: boolean }): Promise<TrainingCategoryDto[]> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();
    if (opts?.includeInactive) params.append('includeInactive', 'true');

    const query = params.toString();
    const url = query ? `${this.baseUrl}/Categories?${query}` : `${this.baseUrl}/Categories`;

    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error('Failed to fetch training categories');
    const result = await response.json();
    return result.data;
  }

  async getCategory(id: number): Promise<TrainingCategoryDto | null> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Categories/${id}`, { headers });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch training category');
    
    const result = await response.json();
    return result.data;
  }

  async createCategory(dto: CreateTrainingCategoryDto): Promise<TrainingCategoryDto> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Categories`, {
      method: 'POST',
      headers,
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to create training category'));
    }
    
    const result = await response.json();
    return result.data;
  }

  async updateCategory(id: number, dto: UpdateTrainingCategoryDto): Promise<TrainingCategoryDto> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Categories/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to update training category'));
    }
    
    const result = await response.json();
    return result.data;
  }

  async deleteCategory(id: number): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Categories/${id}`, { 
      method: 'DELETE',
      headers,
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData );
    }
  }

  // ==================== Training Modules ====================

  async getModules(filters?: {
    franchiseId: number;
    categoryId?: number;
    status?: number;
    searchText?: string;
    includeInactive?: boolean;
  }): Promise<TrainingModuleDto[]> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();
    if (filters?.franchiseId) params.append('franchiseId', filters.franchiseId.toString());
    if (filters?.categoryId) params.append('categoryId', filters.categoryId.toString());
    if (filters?.status !== undefined) params.append('status', filters.status.toString());
    if (filters?.searchText) params.append('searchText', filters.searchText);
    if (filters?.includeInactive) params.append('includeInactive', 'true');

    const response = await fetch(`${this.baseUrl}/Modules?${params}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch training modules');
    
    const result = await response.json();
    return result.data;
  }

  // ==================== Training Module Sections ====================

  async getSectionById(moduleId: number, contentId: number): Promise<TrainingModuleSectionDetailDto | null> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Modules/${moduleId}/Contents/${contentId}`, { headers });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch section details');
    const result = await response.json();
    const detail: TrainingModuleSectionDetailDto = result.data;

    if (!detail.contentText && detail.contentSasUrl) {
      try {
        const blobResponse = await fetch(detail.contentSasUrl);
        if (blobResponse.ok) {
          detail.contentText = await blobResponse.text();
        }
      } catch {
        // blob fetch failed – keep contentText as-is
      }
    }

    return detail;
  }

  async createSection(moduleId: number, dto: CreateSectionRequestDto): Promise<number> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Modules/${moduleId}/Contents`, {
      method: 'POST',
      headers,
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to create section'));
    }
    const result = await response.json();
    return result.id;
  }

  async updateSection(moduleId: number, contentId: number, dto: UpdateSectionRequestDto): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Modules/${moduleId}/Contents/${contentId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to update section'));
    }
  }

  async deleteSection(moduleId: number, contentId: number): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Modules/${moduleId}/Contents/${contentId}`, {
      method: 'DELETE',
      headers,
    });
    if (response.status === 404) throw new Error(`Section ${contentId} not found in module ${moduleId}.`);
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to delete section'));
    }
  }

  // ==================== Training Module Quizzes ====================

  async createQuiz(moduleId: number, dto: CreateQuizStandaloneRequestDto): Promise<number> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Modules/${moduleId}/Quizzes`, {
      method: 'POST',
      headers,
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to create quiz'));
    }
    const result = await response.json();
    return result.id;
  }

  async updateQuiz(moduleId: number, quizId: number, dto: UpdateQuizStandaloneRequestDto): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Modules/${moduleId}/Quizzes/${quizId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to update quiz'));
    }
  }

  async deleteQuiz(moduleId: number, quizId: number): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Modules/${moduleId}/Quizzes/${quizId}`, {
      method: 'DELETE',
      headers,
    });
    if (response.status === 404) throw new Error(`Quiz ${quizId} not found in module ${moduleId}.`);
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to delete quiz'));
    }
  }

  async getModule(id: number): Promise<TrainingModuleDetailDto | null> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Modules/${id}`, { headers });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch training module');
    
    const result = await response.json();
    return result.data;
  }

  async createModule(dto: CreateTrainingModuleDto): Promise<number> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Modules`, {
      method: 'POST',
      headers,
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to create module' }));
      throw new Error(errorData.message || 'Failed to create module');
    }
    const result = await response.json();
    const id = result?.data?.id ?? result?.id;
    if (!id) {
      throw new Error('Create response did not include an id');
    }
    return id;
  }

  async updateModule(id: number, dto: UpdateTrainingModuleDto): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Modules/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to update module' }));
      throw new Error(errorData.message || 'Failed to update module');
    }
  }

  async deleteModule(id: number): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Modules/${id}`, { 
      method: 'DELETE',
      headers,
    });
    if (!response.ok){ 
      const errorData = await response.json();
      throw new Error(errorData );
    }
  }

  async publishModule(id: number): Promise<TrainingModuleDetailDto> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Modules/${id}/publish`, {
      method: 'POST',
      headers,
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to publish module' }));
      throw new Error(errorData.message || 'Failed to publish training module');
    }
    
    const result = await response.json();
    return result.data;
  }

  // ==================== Training Completions ====================

  async getCompletionsByUserId(userId: number): Promise<TrainingAssignmentDto[]> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Completions/user/${userId}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch user training completions');
    const result = await response.json();
    return result.data;
  }

  async getCompletionByAssignmentId(assignmentId: number): Promise<TrainingAssignmentDto | null> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Completions/assignment/${assignmentId}`, { headers });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch training assignment');
    
    const result = await response.json();
    return result.data;
  }

  async startTraining(dto: import('./trainingTypes').StartTrainingDto): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Completions/start`, {
      method: 'POST',
      headers,
      body: JSON.stringify(dto),
    });
    
    if (response.status === 404) {
      throw new Error('Training assignment not found');
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to start training' }));
      throw new Error(errorData.message || 'Failed to start training');
    }
  }

  async submitQuiz(dto: SubmitQuizDto): Promise<QuizResultDto> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Completions/submit-quiz`, {
      method: 'POST',
      headers,
      body: JSON.stringify(dto),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to submit quiz' }));
      throw new Error(errorData.message || 'Failed to submit quiz');
    }
    
    const result = await response.json();
    return result.data;
  }

  async updateProgress(assignmentId: number, currentStep: number): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${this.baseUrl}/Completions/progress/${assignmentId}/step/${currentStep}`,
      {
        method: 'PUT',
        headers,
      },
    );
    
    if (!response.ok) throw new Error('Failed to update training progress');
  }

  // ==================== Training Assignments ====================

  async getAllAssignments(filters: TrainingAssignmentListFilters): Promise<PagedData<TrainingAssignmentDto>> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();
    if (filters?.storeNumber) params.append('storeNumber', filters.storeNumber);
    if (typeof filters.franchiseId === 'number') params.append('franchiseId', String(filters.franchiseId));
    if (filters.search?.trim()) params.append('search', filters.search.trim());
    if (typeof filters.statusId === 'number') params.append('statusId', String(filters.statusId));
    if (typeof filters.skip === 'number') params.append('skip', String(filters.skip));
    if (typeof filters.take === 'number') params.append('take', String(filters.take));

    const response = await fetch(`${this.baseUrl}/Assignments?${params}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch training assignments');
    
    const result = await response.json();
    const pagedResult = result.data;

    return {
      totalCount: pagedResult?.totalCount ?? 0,
      skip: pagedResult?.skip ?? filters.skip ?? 0,
      take: pagedResult?.take ?? filters.take ?? 25,
      data: pagedResult?.data ?? [],
    };
  }

  async getAssignmentById(assignmentId: number): Promise<TrainingAssignmentDto | null> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Assignments/${assignmentId}`, { headers });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch training assignment');
    
    const result = await response.json();
    return result.data;
  }

  async getStoresByFranchise(franchiseId: number): Promise<StoreOptionDto[]> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Assignments/franchise/${franchiseId}`, { headers });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch stores' }));
      throw new Error(errorData.message || 'Failed to fetch stores');
    }

    const result = await response.json();
    return result.data || result || [];
  }

  async getPersonasByStore(storeNumber: string): Promise<PersonaOptionDto[]> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Assignments/personas/by-store/${storeNumber}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch personas by store');
    
    const result = await response.json();
    return result.data;
  }

  async getUsersByStore(storeNumber: string): Promise<UserOptionDto[]> {
    const headers = await this.getHeaders();
    
    const response = await fetch(
      `${this.baseUrl}/Assignments/users/by-store/${storeNumber}`,
      { headers },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch users: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  }

  async getUsersByFranchise(franchiseId: number): Promise<UserOptionDto[]> {
    const headers = await this.getHeaders();

    const response = await fetch(
      `${this.baseUrl}/Assignments/users/by-franchise/${franchiseId}`,
      { headers },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch users by franchise: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  }

  async bulkAssign(dto: import('./trainingTypes').BulkAssignTrainingRequestDto): Promise<import('./trainingTypes').BulkAssignTrainingResponseDto> {
    const headers = await this.getHeaders();
    
    const response = await fetch(`${this.baseUrl}/Assignments/bulk-assign`, {
      method: 'POST',
      headers,
      body: JSON.stringify(dto),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to bulk assign training' }));
      throw new Error(errorData.message || 'Failed to bulk assign training');
    }
    
    const result = await response.json();
    return result.data;
  }

  async deleteAssignment(assignmentId: number): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Assignments/${assignmentId}`, { 
      method: 'DELETE',
      headers,
    });
    
    if (response.status === 404) {
      throw new Error('Assignment not found');
    }
    
    if (!response.ok) throw new Error('Failed to delete training assignment');
  }

  async sendNotification(assignmentId: number, franchiseId?: number, notificationMessage?: string): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/Assignments/${assignmentId}/notify`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ franchiseId: franchiseId ?? null, notificationMessage: notificationMessage || null }),
    });

    if (response.status === 404) {
      const errorData = await response.json().catch(() => ({ message: 'Assignment not found' }));
      throw new Error(errorData.message || 'Assignment not found');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to send notification' }));
      throw new Error(errorData.message || 'Failed to send notification');
    }
  }

  // ==================== Training Certificates ====================

  async getMyCertificates(): Promise<TrainingCertificateDto[]> {
    const headers = await this.getHeaders();
    
    const response = await fetch(`${this.baseUrl}/Certificates/My`, { headers });
    if (!response.ok) throw new Error('Failed to fetch my certificates');
    
    const result = await response.json();
    return result.data;
  }

  async getAllCertificates(filters?: {
    userId?: number;
    moduleId?: number;
    isRevoked?: boolean;
  }): Promise<TrainingCertificateDto[]> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();
    
    if (filters?.userId !== undefined) params.append('userId', filters.userId.toString());
    if (filters?.moduleId !== undefined) params.append('moduleId', filters.moduleId.toString());
    if (filters?.isRevoked !== undefined) params.append('isRevoked', filters.isRevoked.toString());

    const response = await fetch(`${this.baseUrl}/Certificates?${params}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch certificates');
    
    const result = await response.json();
    return result.data;
  }

  async getCertificateById(id: number): Promise<TrainingCertificateDto | null> {
    const headers = await this.getHeaders();
    
    const response = await fetch(`${this.baseUrl}/Certificates/${id}`, { headers });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch certificate');
    
    const result = await response.json();
    return result.data;
  }

  async downloadCertificate(id: number): Promise<Blob> {
    const headers = await this.getHeaders();
    
    const response = await fetch(`${this.baseUrl}/Certificates/${id}/Download`, { headers });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to download certificate' }));
      throw new Error(errorData.message || 'Failed to download certificate');
    }
    
    return await response.blob();
  }

  async verifyCertificate(certificateNumber: string): Promise<TrainingCertificateDto | null> {
    const headers = await this.getHeaders();
    
    const response = await fetch(
      `${this.baseUrl}/Certificates/Verify/${encodeURIComponent(certificateNumber)}`,
      { headers },
    );
    
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to verify certificate');
    
    const result = await response.json();
    return result.data;
  }

  async issueCertificate(completionId: number): Promise<TrainingCertificateDto> {
    const headers = await this.getHeaders();
    
    const response = await fetch(
      `${this.baseUrl}/Certificates/Issue/${completionId}`,
      {
        method: 'POST',
        headers,
      },
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to issue certificate' }));
      throw new Error(errorData.message || 'Failed to issue certificate');
    }
    
    const result = await response.json();
    return result.data;
  }

  async revokeCertificate(id: number, reason: string): Promise<void> {
    const headers = await this.getHeaders();
    
    const response = await fetch(
      `${this.baseUrl}/Certificates/${id}/Revoke`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(reason),
      },
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to revoke certificate' }));
      throw new Error(errorData.message || 'Failed to revoke certificate');
    }
  }

  // ==================== Training Reports ====================

  async generateSummaryReport(filter: import('./trainingTypes').TrainingReportFilterDto): Promise<import('./trainingTypes').TrainingSummaryReportDto> {
    const headers = await this.getHeaders();
    
    const response = await fetch(
      `${this.baseUrl}/Reports/summary/preview`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(filter),
      },
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to generate summary report' }));
      throw new Error(errorData.message || 'Failed to generate summary report');
    }
    
    const result = await response.json();
    return result.data;
  }

  async generateCompletionReport(filter: import('./trainingTypes').TrainingReportFilterDto): Promise<import('./trainingTypes').TrainingCompletionReportDto> {
    const headers = await this.getHeaders();
    
    const response = await fetch(
      `${this.baseUrl}/Reports/completion`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(filter),
      },
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to generate completion report' }));
      throw new Error(errorData.message || 'Failed to generate completion report');
    }
    
    const result = await response.json();
    return result.data;
  }

  async previewSummaryReport(filter: import('./trainingTypes').TrainingReportFilterDto): Promise<string> {
    // Fetch the report data and generate HTML on the client-side
    const reportData = await this.generateSummaryReport(filter);
    return generateSummaryHtmlPreview(reportData);
  }

  async previewCompletionReport(filter: import('./trainingTypes').TrainingReportFilterDto): Promise<string> {
    // Fetch the report data and generate HTML on the client-side
    const reportData = await this.generateCompletionReport(filter);
    return generateCompletionHtmlPreview(reportData);
  }

  async exportSummaryReport(filter: import('./trainingTypes').TrainingReportFilterDto): Promise<Blob> {
    const headers = await this.getHeaders();
    
    const response = await fetch(
      `${this.baseUrl}/Reports/summary/export`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(filter),
      },
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to export summary report' }));
      throw new Error(errorData.message || 'Failed to export summary report');
    }
    
    return await response.blob();
  }

  async exportCompletionReport(filter: import('./trainingTypes').TrainingReportFilterDto): Promise<Blob> {
    const headers = await this.getHeaders();
    
    const response = await fetch(
      `${this.baseUrl}/Reports/completion/export`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(filter),
      },
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to export completion report' }));
      throw new Error(errorData.message || 'Failed to export completion report');
    }
    
    return await response.blob();
  }

  // Training Dashboard
  async getDashboardMetrics(filter: import('./trainingTypes').TrainingDashboardFilter): Promise<import('./trainingTypes').TrainingDashboardMetricsDto> {
    const headers = await this.getHeaders();
    
    const params = new URLSearchParams();
    params.append('franchiseId', filter.franchiseId.toString());
    
    if (filter.storeNumbers && filter.storeNumbers.length > 0) {
      filter.storeNumbers.forEach(store => params.append('storeNumbers', store));
    }
    
    if (filter.regionIds && filter.regionIds.length > 0) {
      filter.regionIds.forEach(region => params.append('regionIds', region));
    }
    
    const response = await fetch(
      `${this.baseUrl}/Dashboard/metrics?${params.toString()}`,
      { headers },
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch dashboard metrics' }));
      throw new Error(errorData.message || 'Failed to fetch dashboard metrics');
    }
    
    const result = await response.json();
    return result.data || result;
  }

  async getCompletionTrends(months: number, filter: import('./trainingTypes').TrainingDashboardFilter): Promise<import('./trainingTypes').TrainingCompletionTrendDto[]> {
    const headers = await this.getHeaders();
    
    const params = new URLSearchParams();
    params.append('franchiseId', filter.franchiseId.toString());
    params.append('months', months.toString());
    
    if (filter.storeNumbers && filter.storeNumbers.length > 0) {
      filter.storeNumbers.forEach(store => params.append('storeNumbers', store));
    }
    
    if (filter.regionIds && filter.regionIds.length > 0) {
      filter.regionIds.forEach(region => params.append('regionIds', region));
    }
    
    const response = await fetch(
      `${this.baseUrl}/Dashboard/completion-trends?${params.toString()}`,
      { headers },
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch completion trends' }));
      throw new Error(errorData.message || 'Failed to fetch completion trends');
    }
    
    const result = await response.json();
    return result.data || result || [];
  }

  async getStorePerformance(filter: import('./trainingTypes').TrainingDashboardFilter): Promise<import('./trainingTypes').StoreTrainingPerformanceDto[]> {
    const headers = await this.getHeaders();
    
    const params = new URLSearchParams();
    params.append('franchiseId', filter.franchiseId.toString());
    
    if (filter.storeNumbers && filter.storeNumbers.length > 0) {
      filter.storeNumbers.forEach(store => params.append('storeNumbers', store));
    }
    
    if (filter.regionIds && filter.regionIds.length > 0) {
      filter.regionIds.forEach(region => params.append('regionIds', region));
    }
    
    const response = await fetch(
      `${this.baseUrl}/Dashboard/store-performance?${params.toString()}`,
      { headers },
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch store performance' }));
      throw new Error(errorData.message || 'Failed to fetch store performance');
    }
    
    const result = await response.json();
    return result.data || result || [];
  }

 
}
